import React from 'react';
import { Project } from '../../services/api';
import { formatEther } from '../../services/web3';
import { useNavigate } from 'react-router-dom';
import { useWeb3 } from '../../hooks/useWeb3';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const navigate = useNavigate();
  const { account } = useWeb3();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'cancelled':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const totalAmount = formatEther(project.totalAmount);
  const depositedAmount = formatEther(project.depositedAmount);
  const progress = project.milestones.filter((m) => m.released).length;
  const totalMilestones = project.milestones.length;

  // Determine user role
  const getUserRole = () => {
    if (!account) return null;
    const isClient = project.client.toLowerCase() === account.toLowerCase();
    const isProvider = project.provider.toLowerCase() === account.toLowerCase();
    if (isClient) return 'Client';
    if (isProvider) return 'Provider';
    const isArbitrator = (project as any).arbitratorAddress && (project as any).arbitratorAddress.toLowerCase() === account.toLowerCase();
    if (isArbitrator) return 'Arbitrator';
    return null;
  };

  const userRole = getUserRole();

  // Calculate milestones requiring approval for current user
  const getPendingApprovals = () => {
    if (!account || project.status === 'completed') return 0;

    const isClient = project.client.toLowerCase() === account.toLowerCase();
    const isProvider = project.provider.toLowerCase() === account.toLowerCase();

    if (!isClient && !isProvider) return 0;
    
    return project.milestones.filter((m) => {
      if (isClient && !m.approvedByClient) return true;
      if (isProvider && !m.approvedByProvider) return true;
      // Check if both approved but not released (can release)
      if (m.approvedByClient && m.approvedByProvider && !m.released) {
        return true;
      }
      return false;
    }).length;
  };

  const pendingApprovals = getPendingApprovals();

  const disputeCount = project.status === 'completed' ? 0 : project.milestones.filter(
    m => m.disputeStatus && m.disputeStatus !== 'none' && m.disputeStatus !== 'resolved'
  ).length;

  return (
    <div
      onClick={() => navigate(`/dashboard/projects/${project.id}`)}
      className={`bg-white border rounded-xl p-6 hover:border-[#FF5555] transition-all duration-300 cursor-pointer relative ${
        pendingApprovals > 0 ? 'border-[#FF3333] border-2 shadow-md' : 'border-[#471b001e]'
      }`}
    >
      {/* Badges */}
      <div className="absolute top-3 right-3 flex gap-2">
        {disputeCount > 0 && (
          <div className="bg-red-100 text-red-700 border border-red-200 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {disputeCount} {disputeCount === 1 ? 'dispute' : 'disputes'}
          </div>
        )}
        {pendingApprovals > 0 && (
          <div className="bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
            {pendingApprovals} {pendingApprovals === 1 ? 'action' : 'actions'}
          </div>
        )}
      </div>

      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 pr-2">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-[#471b00] font-poly">
              {project.title}
            </h3>
            {userRole && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  userRole === 'Client'
                    ? 'bg-blue-100 text-blue-700'
                    : userRole === 'Provider'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-indigo-100 text-indigo-700'
                }`}
              >
                {userRole}
              </span>
            )}
          </div>
          <p className="text-sm text-[#471b007f] font-dm line-clamp-2">
            {project.description}
          </p>
        </div>
        <span
          className={`px-3 py-1 rounded-lg text-xs font-medium border ${getStatusColor(
            project.status
          )}`}
        >
          {project.status.toUpperCase()}
        </span>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-[#471b007f] font-dm">Total Amount</span>
          <span className="text-base font-semibold text-[#471b00] font-poly">
            {parseFloat(totalAmount).toFixed(4)} MATIC
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-[#471b007f] font-dm">Deposited</span>
          <span className="text-base font-medium text-[#471b00] font-dm">
            {parseFloat(depositedAmount).toFixed(4)} MATIC
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-[#471b007f] font-dm">Progress</span>
          <span className="text-base font-medium text-[#471b00] font-dm">
            {progress} / {totalMilestones} milestones
          </span>
        </div>

        <div className="pt-2 border-t border-[#471b001e]">
          <div className="flex justify-between items-center text-xs text-[#471b007f] font-dm">
            <span>Client: {project.client.slice(0, 6)}...{project.client.slice(-4)}</span>
            <span>Provider: {project.provider.slice(0, 6)}...{project.provider.slice(-4)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;

